import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  type ChevronProps,
  DayPicker,
  type DayPickerProps,
  getDefaultClassNames,
} from 'react-day-picker';
import { cn } from './cn';

/**
 * The month grid both date pickers open into.
 *
 * It knows nothing about the current language: the app passes a date-fns
 * `locale` down, the same way it passes any other data into this package. That
 * keeps the shared components free of an i18n dependency, which is what lets
 * the admin console — which has no i18n at all — use them.
 *
 * The range colours live in `calendar.css` rather than here. react-day-picker
 * marks the *cell* as range start, middle or end while the thing that needs
 * painting is the button inside it, and a descendant selector is the honest way
 * to say that; expressing it as utilities would mean a class on every day.
 */

export type CalendarProps = DayPickerProps;

function CalendarChevron({ orientation, className, ...rest }: ChevronProps) {
  const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
  return <Icon className={cn('h-4 w-4', className)} {...rest} />;
}

const COMPONENTS = { Chevron: CalendarChevron };

export function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  weekStartsOn = 1,
  ...props
}: CalendarProps) {
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      className={cn('p-3', className)}
      classNames={{
        ...defaults,
        root: cn(defaults.root, 'w-fit'),
        months: cn(defaults.months, 'flex flex-col sm:flex-row gap-6'),
        month: cn(defaults.month, 'flex flex-col gap-3'),
        month_caption: cn(defaults.month_caption, 'flex h-8 items-center justify-start px-1'),
        caption_label: cn(defaults.caption_label, 'text-sm font-semibold text-foreground'),
        // Both arrows sit together at the top right. Spread to the two edges
        // they land on top of the month name once `numberOfMonths` is 2, which
        // is how the range picker opens.
        nav: cn(defaults.nav, 'absolute right-3 top-3 flex h-8 items-center gap-1'),
        button_previous: cn(defaults.button_previous, NAV_BUTTON),
        button_next: cn(defaults.button_next, NAV_BUTTON),
        month_grid: cn(defaults.month_grid, 'border-collapse'),
        weekdays: cn(defaults.weekdays, 'flex'),
        weekday: cn(
          defaults.weekday,
          'w-9 text-[0.7rem] font-normal uppercase tracking-wide text-muted-foreground'
        ),
        week: cn(defaults.week, 'mt-1 flex w-full'),
        day: cn(defaults.day, 'h-9 w-9 p-0 text-center text-sm'),
        day_button: cn(
          defaults.day_button,
          'flex h-9 w-9 items-center justify-center rounded-full font-normal text-foreground',
          'transition-colors hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          'disabled:pointer-events-none'
        ),
        today: defaults.today, // painted in calendar.css, on the button

        outside: cn(defaults.outside, 'text-muted-foreground/50'),
        disabled: cn(defaults.disabled, 'text-muted-foreground/40'),
        hidden: cn(defaults.hidden, 'invisible'),
        ...classNames,
      }}
      components={COMPONENTS}
      {...props}
    />
  );
}

/**
 * 36px, so the arrows clear the 44px-minus-padding floor the accessibility
 * rules set for a touch target while still reading as chrome next to the month.
 */
const NAV_BUTTON = cn(
  'inline-flex h-9 w-9 items-center justify-center rounded-full',
  'text-muted-foreground transition-colors',
  'hover:bg-accent hover:text-foreground',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  'disabled:pointer-events-none disabled:opacity-40'
);

Calendar.displayName = 'Calendar';
